import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Form } from 'antd';
import { useNavigate } from 'react-router-dom';
import { isUndefined } from 'lodash';
import PersonForm from './PersonForm.jsx';
import {
  useGetUserQuery,
  useSaveUserMutation,
} from '../../../../../redux/services/users';
import { formattedFullName } from '../../../../../utils/helpers/CommonHelper';
import { routes } from '../../../../../utils/helpers/ApiClient';
import { AppBarCMS } from '../../../../basic';
import { CollapseBlock, Spinner } from '../../../../common';
import { useToastMessages } from '../../../../../hooks/useToastMessages';
import {
  FileTextOutlined,
  LinkOutlined,
  PlusOutlined,
} from '@ant-design/icons';

const PersonProfile = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const userId = useSelector((state) => state.auth?.user?.id);
  const userRoles = useSelector((state) => state.auth?.user?.roles);

  const { data: userDetail, isLoading, refetch } = useGetUserQuery(userId, { skip: !userId });
  const [saveUser, { isSuccess: isSaveSuccess }] = useSaveUserMutation();

  const { saveSuccess } = useToastMessages();

  const [form] = Form.useForm();

  const isStudent =
    userDetail?.teacher === null || userDetail?.teacher === undefined;

  useEffect(() => {
    document.title = t('Profil') + ' - FRI CMS';
  }, []); // Runs on mount

  useEffect(() => {
    if (isSaveSuccess) {
      saveSuccess();
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSaveSuccess]);

  const initials = useMemo(() => {
    if (isUndefined(userDetail)) return {};

    const userInitials = {
      name: formattedFullName(userDetail),
      mail: userDetail?.mail,
      newsletters: userDetail?.newsletters,
      ldap_mail: userDetail?.ldap_mail,
    };

    return isStudent
      ? {
          ...userInitials,
          school_group: userDetail.activeStudent.school_group,
        }
      : {
          ...userInitials,
          phone: userDetail?.teacher?.phone,
          room: userDetail?.teacher?.room,
          description_sk: userDetail?.teacher?.description_sk,
          description_en: userDetail?.teacher?.description_en,
          homepage: userDetail?.teacher?.homepage,
        };
  }, [userDetail, isStudent]);

  const userRolesLabel = useMemo(() => {
    if (!Array.isArray(userRoles) || userRoles.length === 0) {
      return '-';
    }

    return userRoles
      .map((role) => role?.name)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b, 'sk'))
      .join(', ');
  }, [userRoles]);

  const mainActions = [
    {
      onClick: () => navigate(routes.cmsNewsCreate),
      title: t('Pridať aktualitu'),
      permission: 'news.store',
      icon: <PlusOutlined />,
    },
    {
      onClick: () => navigate(routes.cmsArticleCreate),
      title: t('Pridať článok'),
      permission: 'pages.store',
      icon: <FileTextOutlined />,
    },
    {
      onClick: () => navigate(routes.cmsUsefulLinksCreate),
      title: t('Pridať užitočný odkaz'),
      permission: 'useful-links.store',
      icon: <LinkOutlined />,
    },
    {
      onClick: () =>
        i18n.changeLanguage(i18n.resolvedLanguage === 'en' ? 'sk' : 'en'),
      title:
        i18n.resolvedLanguage === 'en'
          ? t('Zmeniť jazyk na slovenčinu')
          : t('Change language to English'),
      icon: <i className="fas fa-language ml-1 mr-1" />,
    },
  ];

  return isLoading ? (
    <Spinner />
  ) : (
    <>
      <AppBarCMS title={t('Profil')} mainActions={mainActions} />
      <div style={{ margin: '10px' }}>
        <CollapseBlock>
          <PersonForm
            isStudent={isStudent}
            initials={initials}
            userRolesLabel={userRolesLabel}
            onSubmit={(values) => {
              saveUser({ body: values, id: userId });
            }}
            form={form}
            userId={userId}
          />
        </CollapseBlock>
      </div>
    </>
  );
};

export default PersonProfile;
